import { supabase, applyOrgFilter, EVENTS_ENABLED } from '../supabaseClient'
import { getMexicoDateString } from '../dateUtils'
import { getGroupPlayers } from './groups'

// Check which groups have attendance records for today (batch)
export const getGroupsWithAttendanceToday = async (groupIds = []) => {
  if (!groupIds.length) return { data: new Set(), error: null }
  const today = getMexicoDateString()
  let query = supabase
    .from('asistencias')
    .select('grupo_id')
    .in('grupo_id', groupIds)
    .eq('fecha', today)
  query = applyOrgFilter(query)
  const { data, error } = await query
  if (error) return { data: new Set(), error }
  const ids = new Set((data || []).map((r) => r.grupo_id))
  return { data: ids, error: null }
}

export const getAttendanceStatsByGroup = async ({ groupId, startDate = null, endDate = null } = {}) => {
  if (!groupId) return { data: [], error: null }

  let query = supabase
    .from('asistencias')
    .select(`
      presente,
      sesion:sesiones(id, serie_entrenamiento_id, estatus, tipo)
    `)
    .eq('grupo_id', groupId)

  if (startDate) query = query.gte('fecha', startDate)
  if (endDate) query = query.lte('fecha', endDate)

  query = applyOrgFilter(query)
  const { data, error } = await query
  if (error) {
    console.error('[getAttendanceStatsByGroup] Error:', error)
    return { data: [], error }
  }
  return { data: data || [], error: null }
}

export const saveAttendance = async (attendanceData) => {
  try {
    // Handle both single record and array
    const recordsArray = Array.isArray(attendanceData) ? attendanceData : [attendanceData];

    const formattedRecords = recordsArray.map(record => {
      const descripcionFallback = record.juego_id
        ? record.descripcion || 'Asistencia de juego'
        : record.descripcion || 'Asistencia de entrenamiento';
      const staffRef = record.staff_id || record.coach_id || null;
      return {
        jugador_id: record.jugador_id, // UUID after migration
        grupo_id: record.grupo_id, // UUID after migration
        sesion_id: record.sesion_id || record.evento_id || record.juego_id || null,
        evento_id: record.evento_id || null, // compat
        juego_id: record.juego_id || null, // compat
        fecha: record.fecha,
        presente: record.presente,
        descripcion: descripcionFallback,
        staff_id: staffRef // UUID
      };
    });

    // Process each record individually with proper upsert logic
    const results = [];

    for (const record of formattedRecords) {
      let result;

      if (record.sesion_id) {
        const { data: existing } = await supabase
          .from('asistencias')
          .select('id')
          .eq('jugador_id', record.jugador_id)
          .eq('sesion_id', record.sesion_id)
          .maybeSingle();

        if (existing) {
          const { data, error } = await supabase
            .from('asistencias')
            .update({
              presente: record.presente,
              descripcion: record.descripcion,
              fecha: record.fecha,
              staff_id: record.staff_id
            })
            .eq('id', existing.id)
            .select()
            .single();

          if (error) throw error;
          result = data;
        } else {
          const { data, error } = await supabase
            .from('asistencias')
            .insert(record)
            .select()
            .single();

          if (error) throw error;
          result = data;
        }
      } else if (record.evento_id && !record.juego_id) {
        // Check if attendance record exists for this evento
        const { data: existing } = await supabase
          .from('asistencias')
          .select('id')
          .eq('jugador_id', record.jugador_id)
          .eq('evento_id', record.evento_id)
          .eq('fecha', record.fecha)
          .maybeSingle();

        if (existing) {
          // Update existing record
          const { data, error } = await supabase
            .from('asistencias')
            .update({
              presente: record.presente,
              descripcion: record.descripcion,
              staff_id: record.staff_id
            })
            .eq('id', existing.id)
            .select()
            .single();

          if (error) throw error;
          result = data;
        } else {
          // Insert new record
          const { data, error } = await supabase
            .from('asistencias')
            .insert(record)
            .select()
            .single();

          if (error) throw error;
          result = data;
        }
      } else if (record.juego_id && !record.evento_id) {
        // Check if attendance record exists for this juego
        const { data: existing } = await supabase
          .from('asistencias')
          .select('id')
          .eq('jugador_id', record.jugador_id)
          .eq('juego_id', record.juego_id)
          .maybeSingle();

        if (existing) {
          // Update existing record
          const { data, error } = await supabase
            .from('asistencias')
            .update({
              presente: record.presente,
              fecha: record.fecha,
              descripcion: record.descripcion,
              staff_id: record.staff_id
            })
            .eq('id', existing.id)
            .select()
            .single();

          if (error) throw error;
          result = data;
        } else {
          // Insert new record
          const { data, error } = await supabase
            .from('asistencias')
            .insert(record)
            .select()
            .single();

          if (error) throw error;
          result = data;
        }
      }

      if (result) {
        results.push(result);
      }
    }

    return { data: results, error: null };

  } catch (err) {
    console.error('Error in saveAttendance:', err);
    return { data: null, error: err };
  }
}

export const getEventAttendance = async (eventId) => {
  let query = supabase
    .from('asistencias')
    .select(`
      *,
      jugadores (
        id,
        nombre_completo,
        numero
      )
    `)
    .eq('sesion_id', eventId)
  query = applyOrgFilter(query)
  const { data, error } = await query

  return { data, error }
}

export const getTodayAttendance = async (groupId, staffId) => {
  const today = getMexicoDateString()

  let query = supabase
    .from('asistencias')
    .select('*')
    .eq('grupo_id', groupId)
    .eq('staff_id', staffId)
    .eq('fecha', today)
  query = applyOrgFilter(query)
  const { data, error } = await query

  return { data, error }
}

export const clearAttendanceForEvent = async (eventoId) => {
  try {
    const { data, error } = await supabase
      .from('asistencias')
      .delete()
      .eq('sesion_id', eventoId);

    return { data, error };
  } catch (error) {
    console.error('Error clearing attendance:', error);
    return { data: null, error };
  }
};

// Dashboard statistics
export const getGroupAttendanceStats = async (staffId, startDate, endDate) => {
  return await getGroupAttendanceStatsFallback(staffId, startDate, endDate);
}

// Fallback function using regular queries
const getGroupAttendanceStatsFallback = async (staffId, startDate, endDate) => {
  try {
    // Validate dates to prevent empty date queries
    if (!startDate || !endDate) {

      return { data: [], error: null }
    }
    // First check if this is an admin by getting the current user
    const { data: { user } } = await supabase.auth.getUser();
    const isAdmin = user?.user_metadata?.rol === 'admin';

    let staffGroups;
    let groupsError;

    if (isAdmin) {
      // Admin gets all groups
      let gruposQuery = supabase
        .from('grupos')
        .select('*')
        .in('estatus', ['Activo', 'Inactivo', 'En preparación']);
      gruposQuery = applyOrgFilter(gruposQuery);
      const result = await gruposQuery;

      staffGroups = result.data?.map(group => ({ grupos: group })) || [];
      groupsError = result.error;
    } else {
      // Regular staff gets assigned groups
      const result = await supabase
        .from('staff_grupos')
        .select('grupo_id, grupos!inner(*)')
        .eq('staff_id', staffId)
        .eq('activo', true)
        .in('grupos.estatus', ['Activo', 'Inactivo', 'En preparación']);

      staffGroups = result.data || [];
      groupsError = result.error;
    }

    if (groupsError) throw groupsError

    const stats = []

    for (const staffGroup of staffGroups || []) {
      const grupo = staffGroup.grupos
      // Get all training sessions for this group in date range
      let sessionsQuery = supabase
        .from('sesiones')
        .select('id, fecha')
        .eq('grupo_id', grupo.id)
        .eq('tipo', 'entrenamiento')
        .gte('fecha', startDate)
        .lte('fecha', endDate)
        .neq('estatus', 'cancelado');
      sessionsQuery = applyOrgFilter(sessionsQuery);
      const { data: sessions, error: sessionsError } = await sessionsQuery;

      if (sessionsError) {

        continue;
      }

      // Get active players for this group
      const { data: groupPlayers, error: playersError } = await getGroupPlayers(grupo.id);
      const activePlayersCount = groupPlayers?.length || 0;

      // Get attendance records for this group in date range
      let attendanceQuery = supabase
        .from('asistencias')
        .select('presente, jugador_id, fecha, evento_id')
        .eq('grupo_id', grupo.id)
        .gte('fecha', startDate)
        .lte('fecha', endDate)
      attendanceQuery = applyOrgFilter(attendanceQuery)
      const { data: attendance, error: attendanceError } = await attendanceQuery

      if (attendanceError) {

        continue;
      }

      let presente_count = 0, retardo_count = 0, ausente_count = 0, exento_count = 0;

      // Count actual attendance records
      attendance?.forEach(a => {
        if (a.presente === 'presente') presente_count++
        else if (a.presente === 'retardo') retardo_count++
        else if (a.presente === 'ausente') ausente_count++
        else if (a.presente === 'exento') exento_count++
      })

      // Calculate missing attendance (players who didn't have any record = ausente)
      const baseExpectedRecords = activePlayersCount * (sessions?.length || 0);
      const totalExpectedRecords = Math.max(0, baseExpectedRecords - exento_count);
      const actualRecords = Math.max(0, (attendance?.length || 0) - exento_count);
      const missingRecords = Math.max(0, totalExpectedRecords - actualRecords);

      // Add missing records as ausente
      ausente_count += missingRecords;

      const groupStats = {
        grupo_id: grupo.id,
        grupo_nombre: grupo.nombre_del_grupo,
        total_jugadores: activePlayersCount,
        presente_count,
        retardo_count,
        ausente_count
      };

      stats.push(groupStats);
    }

    return { data: stats, error: null }
  } catch (error) {

    return { data: null, error }
  }
}

export const getPlayerAttendanceStats = async (groupId, startDate, endDate, eventType = null) => {
  return await getPlayerAttendanceStatsFallback(groupId, startDate, endDate, eventType);
}

// Fallback function for player stats
const getPlayerAttendanceStatsFallback = async (groupId, startDate, endDate, eventType = null) => {
  try {
    // Validate dates to prevent empty date queries
    if (!startDate || !endDate) {

      return [];
    }
    // Get group players
    const { data: groupPlayers, error: playersError } = await getGroupPlayers(groupId)
    if (playersError) throw playersError

    const tipoSesion = eventType && eventType !== 'todos' ? eventType : 'entrenamiento';
    // Get all training sessions for this group in date range
    let query = supabase
      .from('sesiones')
      .select('id, fecha')
      .eq('grupo_id', groupId)
      .eq('tipo', tipoSesion)
      .gte('fecha', startDate)
      .lte('fecha', endDate)
      .neq('estatus', 'cancelado');

    query = applyOrgFilter(query);
    const { data: sessions, error: sessionsError } = await query;
    if (sessionsError) throw sessionsError;

    const stats = []

    for (const playerGroup of groupPlayers || []) {
      const player = playerGroup.jugadores

      // Get attendance for this player in date range
      let attendanceQuery = supabase
        .from('asistencias')
        .select('presente, evento_id, fecha')
        .eq('jugador_id', player.id)
        .eq('grupo_id', groupId)
        .gte('fecha', startDate)
        .lte('fecha', endDate)
      attendanceQuery = applyOrgFilter(attendanceQuery)
      const { data: attendance, error: attendanceError } = await attendanceQuery

      if (attendanceError) continue

      let presente_count = 0, retardo_count = 0, ausente_count = 0, exento_count = 0

      // Count actual attendance records
      attendance?.forEach(a => {
        if (a.presente === 'presente') presente_count++
        else if (a.presente === 'retardo') retardo_count++
        else if (a.presente === 'ausente') ausente_count++
        else if (a.presente === 'exento') exento_count++
      })

      // Calculate missing attendance (events without records = ausente)
      const baseExpectedRecords = sessions?.length || 0;
      const totalExpectedRecords = Math.max(0, baseExpectedRecords - exento_count);
      const actualRecords = Math.max(0, (attendance?.length || 0) - exento_count);
      const missingRecords = Math.max(0, totalExpectedRecords - actualRecords);

      // Add missing records as ausente
      ausente_count += missingRecords;

      stats.push({
        jugador_id: player.id,
        nombre_completo: player.nombre_completo,
        nickname: player.nickname,
        numero: player.numero,
        presente_count,
        retardo_count,
        ausente_count
      })
    }

    return { data: stats, error: null }
  } catch (error) {

    return { data: null, error }
  }
}

export const getUniqueEventsCount = async (staffId, startDate, endDate) => {
  return await getUniqueEventsCountFallback(staffId, startDate, endDate);
}

// Fallback function for events count
const getUniqueEventsCountFallback = async (staffId, startDate, endDate) => {
  try {
    // Validate dates to prevent empty date queries
    if (!startDate || !endDate) {

      return { data: [{ unique_events_count: 0 }], error: null };
    }
    // Get staff's groups first
    const { data: staffGroups, error: groupsError } = await supabase
      .from('staff_grupos')
      .select('grupo_id')
      .eq('staff_id', staffId)
      .eq('activo', true)

    if (groupsError) throw groupsError

    if (!staffGroups || staffGroups.length === 0) {
      return { data: [{ unique_events_count: 0 }], error: null }
    }

    const groupIds = staffGroups.map(sg => sg.grupo_id)

    // Count training sessions in staff's groups within date range
    let sessionsQuery = supabase
      .from('sesiones')
      .select('id')
      .in('grupo_id', groupIds)
      .eq('tipo', 'entrenamiento')
      .gte('fecha', startDate)
      .lte('fecha', endDate)
      .neq('estatus', 'cancelado')
    sessionsQuery = applyOrgFilter(sessionsQuery)
    const { data: sessions, error: sessionsError } = await sessionsQuery

    if (sessionsError) throw sessionsError

    return {
      data: [{ unique_events_count: sessions?.length || 0 }],
      error: null
    }
  } catch (error) {
    return { data: [{ unique_events_count: 0 }], error }
  }
}

// History
export const getAttendanceHistory = async (staffId, startDate, endDate) => {
  if (!EVENTS_ENABLED) {
    return { data: [], error: null };
  }
  // Validate dates to prevent empty date queries
  if (!startDate || !endDate) {

    return { data: [], error: null };
  }

  let query = supabase
    .from('asistencias')
    .select(`
      *,
      jugadores!inner(nombre_completo, nickname, numero),
      grupos!inner(nombre_del_grupo)
    `)
    .eq('staff_id', staffId)
    .gte('fecha', startDate)
    .lte('fecha', endDate)
    .order('fecha', { ascending: false })
  query = applyOrgFilter(query)
  const { data, error } = await query

  return { data, error }
}

export const getAttendanceLogsForEvent = async (eventId) => {
  try {
    const { data, error } = await supabase
      .rpc('get_attendance_logs', { evento_uuid: eventId });

    return { data, error };
  } catch (error) {

    return { data: null, error };
  }
};
